import type { RouterOutputs } from "@repo/api";
import { formatIntToCurrency } from "@repo/utils";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import type { ColumnDefWithTotal } from "~/types";
import { DataTable } from "~/components/miscellaneous/data-table";
import { DataTableColumnHeader } from "~/components/miscellaneous/data-table-column-header";
import { api } from "~/lib/api";
import { useReportStore } from "~/stores/use-report-store";

export const Route = createFileRoute("/_main/reports/_layout/category")({
  component: () => <CategoryReports />,
});

type Report = RouterOutputs["report"]["getCategoryReports"][0];

function CategoryReports() {
  const { range } = useReportStore();

  const { t } = useTranslation("common");

  const { data: reportData } = api.report.getCategoryReports.useQuery({
    start: range.from,
    end: range.to,
  });

  const columns: ColumnDefWithTotal<Report>[] = [
    {
      id: t("Report.Category.name"),
      accessorKey: "categoryName",
      filterFn: "includesString",
      size: 10,
      enableTotal: true,
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("Report.employee_name")}
        />
      ),
      cell: ({ row: { original: report } }) => {
        return <p>{report.categoryName}</p>;
      },
    },
    {
      id: t("Report.Category.items_sold"),
      accessorKey: "itemsSold",
      filterFn: "includesString",
      size: 10,
      enableTotal: true,
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("Report.Category.items_sold")}
        />
      ),
      cell: ({ row: { original: report } }) => {
        return <p>{report.itemsSold}</p>;
      },
    },
    {
      id: t("Report.Category.revenue"),
      accessorKey: "revenue",
      filterFn: "includesString",
      size: 10,
      enableTotal: true,
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("Report.Category.revenue")}
        />
      ),
      cell: ({ row: { original: report } }) => {
        return <p>{formatIntToCurrency(report.revenue, false)}</p>;
      },
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <DataTable
        defaultSorting={[{ id: t("Report.Category.name"), desc: false }]}
        pdfExport={{
          filename: "category-report",
        }}
        csvExport={{
          filename: "category-report",
        }}
        columns={columns}
        data={reportData ?? []}
      />
    </div>
  );
}
